import { useCallback, useMemo, useState } from "react";

export default function usePagination(config) {
  const {
    initialPage = 1,
    initialPageSize = 25,
    initialTotalCount = 0,
    initialFilters = {},
    minPageSize = 1,
    maxPageSize = Number.MAX_SAFE_INTEGER,
  } = config || {};

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [filters, setFiltersState] = useState(initialFilters);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);

  const totalPages = useMemo(
    () => (pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1),
    [totalCount, pageSize]
  );

  const hasNextPage = useMemo(
    () => page < totalPages || !!nextCursor,
    [page, totalPages, nextCursor]
  );

  const hasPreviousPage = useMemo(
    () => page > 1 || !!previousCursor,
    [page, previousCursor]
  );

  const clampPage = useCallback(
    (p) => {
      if (p < 1) return 1;
      if (p > totalPages) return totalPages;
      return p;
    },
    [totalPages]
  );

  const setPage = useCallback(
    (p) => {
      setPageState((prev) => {
        const next = clampPage(p);
        return next === prev ? prev : next;
      });
    },
    [clampPage]
  );

  const goNext = useCallback(() => {
    if (!hasNextPage) return;
    setPageState((prev) => prev + 1);
  }, [hasNextPage]);

  const goPrevious = useCallback(() => {
    if (!hasPreviousPage) return;
    setPageState((prev) => Math.max(1, prev - 1));
  }, [hasPreviousPage]);

  const setPageSize = useCallback(
    (size) => {
      const clamped = Math.min(Math.max(size, minPageSize), maxPageSize);
      setPageSizeState(clamped);
      setPageState(1);
    },
    [minPageSize, maxPageSize]
  );

  const setFilters = useCallback((updater) => {
    setFiltersState((prev) =>
      typeof updater === "function" ? updater(prev) : { ...prev, ...updater }
    );
    setPageState(1);
  }, []);

  // Call this with backend response meta: { totalCount, next, previous }
  const syncWithBackend = useCallback((meta) => {
    if (typeof meta?.totalCount === "number") {
      setTotalCount(meta?.totalCount);
    }
    if ("next" in meta) setNextCursor(meta?.next ?? null);
    if ("previous" in meta) setPreviousCursor(meta?.previous ?? null);
  }, []);

  const reset = useCallback(() => {
    setPageState(initialPage);
    setPageSizeState(initialPageSize);
    setTotalCount(initialTotalCount);
    setFiltersState(initialFilters);
    setNextCursor(null);
    setPreviousCursor(null);
  }, [initialFilters, initialPage, initialPageSize, initialTotalCount]);

  return {
    page,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    nextCursor,
    previousCursor,
    filters,
    setPage,
    goNext,
    goPrevious,
    setPageSize,
    setFilters,
    syncWithBackend,
    reset,
  };
}
